import type { ReactNode } from 'react';
import { Copy, Forward, ListPlus, Send } from '@boxicons/react';
import useSession from '../../../hooks/useSession';
import useAddListLevelModal from '../../../hooks/modals/useAddListLevelModal';
import useContextMenu from '../../../context/menu/useContextMenu';
import Level from '../../../components/shared/Level';
import { GridLevel } from '../../../components/shared/GridLevel';
import { copyText } from '../../../utils/copyText';
import type { GetLevelsResponse } from '@gdladder/shared/features';

type LevelItemProps = {
    level: GetLevelsResponse['data'][0];
    isSelected: boolean;
    userId?: number;
};

type LevelPresenterProps = {
    levels: GetLevelsResponse['data'];
    selectedLevel?: number;
    className?: string;
    renderLevel: (props: LevelItemProps) => ReactNode;
};

function LevelPresenter({ levels, selectedLevel, className, renderLevel }: LevelPresenterProps) {
    const session = useSession();

    return (
        <div className={className}>
            {levels.map((level, i) =>
                renderLevel({
                    level,
                    isSelected: i + 1 === selectedLevel,
                    userId: session.user?.ID,
                }),
            )}
        </div>
    );
}

export function ListLevelPresenter({
    levels,
    selectedLevel,
}: {
    levels: GetLevelsResponse['data'];
    selectedLevel?: number;
}) {
    return (
        <LevelPresenter
            levels={levels}
            selectedLevel={selectedLevel}
            className='mt-2 space-y-2'
            renderLevel={({ level, isSelected, userId }) => (
                <ListLevel key={level.id} level={level} isSelected={isSelected} userId={userId} />
            )}
        />
    );
}

export function GridLevelPresenter({
    levels,
    selectedLevel,
}: {
    levels: GetLevelsResponse['data'];
    selectedLevel?: number;
}) {
    return (
        <LevelPresenter
            levels={levels}
            selectedLevel={selectedLevel}
            className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 my-2'
            renderLevel={({ level, isSelected, userId }) => (
                <GridLevelItem key={level.id} level={level} isSelected={isSelected} userId={userId} />
            )}
        />
    );
}

function useLevelContextMenu(levelId: number, userId?: number) {
    const openAddToListModal = useAddListLevelModal();
    return useContextMenu([
        { text: 'Open', to: `/level/${levelId}`, icon: <Forward size='sm' /> },
        { text: 'Submit', to: `/submit/${levelId}`, requireSession: true, icon: <Send size='sm' /> },
        {
            text: 'Add to list',
            onClick: () => openAddToListModal(userId!, levelId),
            requireSession: true,
            icon: <ListPlus size='sm' />,
        },
        { type: 'divider' },
        { text: 'Copy ID', onClick: () => copyText(levelId.toString()), icon: <Copy size='sm' /> },
    ]);
}

function ListLevel({ level, isSelected, userId }: LevelItemProps) {
    const onContextMenu = useLevelContextMenu(level.id, userId);

    return (
        <Level
            ID={level.id}
            difficulty={level.difficulty}
            enjoyment={level.enjoyment}
            rarity={level.rarity}
            rating={level.rating}
            name={level.name}
            creator={level.publisherName}
            songName={level.songName}
            selected={isSelected}
            completed={level.isComplete === 1}
            onContextMenu={onContextMenu}
        />
    );
}

function GridLevelItem({ level, isSelected, userId }: LevelItemProps) {
    const onContextMenu = useLevelContextMenu(level.id, userId);

    return (
        <GridLevel
            ID={level.id}
            difficulty={level.difficulty}
            enjoyment={level.enjoyment}
            rarity={level.rarity}
            rating={level.rating}
            name={level.name}
            creator={level.publisherName}
            selected={isSelected}
            completed={level.isComplete === 1}
            proof={level.showcase ? `https://youtu.be/${level.showcase}` : undefined}
            inPack={level.isInPack === 1}
            onContextMenu={onContextMenu}
        />
    );
}
